import { z } from 'zod';
import { posProductSearchResultSchema } from './catalog-responses';
import { posProductRecognitionDecisionSchema } from './admin-inputs';

/** Identification only: prices, suppliers and fiscal controls do not establish identity. */
export const posProductRecognitionDraftSchema = z.object({
  name: z.string().max(500).default(''),
  description: z.string().max(10000).nullish(),
  sku: z.string().max(100).nullish(),
  ean: z.string().max(50).nullish(),
  alternate_eans: z.array(z.string().max(50)).max(100).default([]),
  brand_id: z.string().nullish(),
  variant_info: z.string().nullish(),
  attributes: z.record(z.string()).default({}),
});
export const posProductRecognitionRequestSchema = z.object({
  draft: posProductRecognitionDraftSchema,
  enrich: z.boolean().default(false),
});
export const posProductRecognitionCandidateSchema = z.object({
  product: posProductSearchResultSchema,
  attributes: z.record(z.string()),
  reasons: z.array(z.enum(['ean', 'sku', 'name', 'description', 'semantic'])),
  differences: z.array(z.string()),
  usable: z.boolean(),
});
export const posProductRecognitionResponseSchema = z.object({
  review_id: z.string().uuid(),
  draft_hash: z.string(),
  candidates: z.array(posProductRecognitionCandidateSchema),
  deterministic: z.literal('complete'),
  semantic: z.enum(['not_requested', 'complete', 'unavailable']),
  coverage: z.enum(['complete', 'partial']),
  message: z.string().nullable(),
});
export const posProductReuseInputSchema = z.object({
  operation_id: z.string().uuid(),
  review_id: z.string().uuid(),
  draft_hash: z.string(),
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable().default(null),
  /** Only explicitly declared EANs. Unknown scanner strings are not aliases. */
  add_eans: z.array(z.string().trim().min(1).max(50)).max(100).default([]),
  supplier_mapping: z.object({
    supplier_id: z.string().uuid(),
    supplier_sku: z.string().trim().min(1).max(100),
  }).optional(),
});
export const posProductResolutionResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('created'), product: posProductSearchResultSchema }),
  z.object({ kind: z.literal('reused'), product: posProductSearchResultSchema }),
  z.object({ kind: z.literal('cancelled') }),
]);
export const posProductRecognitionErrorCodeSchema = z.enum([
  'product_recognition_required', 'product_recognition_stale',
  'product_operation_conflict', 'product_target_unavailable',
]);
export type PosProductRecognitionDraft = z.infer<typeof posProductRecognitionDraftSchema>;
export type PosProductRecognitionRequest = z.infer<typeof posProductRecognitionRequestSchema>;
export type PosProductRecognitionResponse = z.infer<typeof posProductRecognitionResponseSchema>;
export type PosProductRecognitionCandidate = z.infer<typeof posProductRecognitionCandidateSchema>;
export type PosProductRecognitionDecision = z.infer<typeof posProductRecognitionDecisionSchema>;
export type PosProductReuseInput = z.infer<typeof posProductReuseInputSchema>;
export type PosProductResolutionResult = z.infer<typeof posProductResolutionResultSchema>;